import { forwardRef, useEffect, useMemo, useState } from "react";
import { Button, Card, Dropdown, Form, InputGroup, Table } from "react-bootstrap";

const RowMenuToggle = forwardRef<
  HTMLButtonElement,
  { children?: React.ReactNode; onClick?: (e: React.MouseEvent) => void; "aria-expanded"?: boolean }
>(({ children, onClick, "aria-expanded": expanded }, ref) => (
  <button
    ref={ref}
    type="button"
    aria-haspopup="menu"
    aria-expanded={expanded}
    aria-label="Row actions"
    className="btn btn-sm btn-icon row-menu-toggle"
    onClick={(e) => {
      e.preventDefault();
      onClick?.(e);
    }}
  >
    {children}
  </button>
));
import { OrganizationService } from "@services/organizations/OrganizationService.ts";
import { organizationStore } from "@store/OrganizationStore.ts";
import { appStore } from "@store/appStore.ts";
import { useSnapshot } from "valtio";
import { Organization } from "@models/organization.model.ts";
import CreateOrganization from "./createOrganization";
import Message from "../../components/common/Message";
import MoveToParentModal from "./components/MoveToParentModal";
import OrgDeleteModal from "./components/OrgDeleteModal";
import { useNavigate, useSearchParams } from "react-router-dom";
import { Paths } from "../../paths";
import useOrganization from "./hooks/useOrganization";
import { useDocumentTitle } from "@/shared/hooks/useDocumentTitle";
import showAlert from "@/shared/utils/generic";
import "./organizations.css";

type OrgNode = { org: Organization; children: OrgNode[] };

function buildTree(orgs: Organization[]): OrgNode[] {
  const byId = new Map<number, OrgNode>();
  orgs.forEach((o) => byId.set(o.id, { org: o, children: [] }));
  const roots: OrgNode[] = [];
  for (const o of orgs) {
    const node = byId.get(o.id)!;
    if (o.parent_id != null && byId.has(o.parent_id)) {
      byId.get(o.parent_id)!.children.push(node);
    } else {
      roots.push(node);
    }
  }
  const sortRec = (arr: OrgNode[]) => {
    arr.sort((a, b) => a.org.name.localeCompare(b.org.name));
    arr.forEach((n) => sortRec(n.children));
  };
  sortRec(roots);
  return roots;
}

// Prune the tree to only branches containing a match. Returns matching ancestor ids
// so the caller can auto-expand them.
function pruneTree(tree: OrgNode[], term: string): { tree: OrgNode[]; expandIds: Set<number> } {
  const expandIds = new Set<number>();
  if (!term.trim()) return { tree, expandIds };
  const lower = term.toLowerCase();

  const visit = (node: OrgNode): OrgNode | null => {
    const selfMatch = node.org.name.toLowerCase().includes(lower);
    const keptChildren: OrgNode[] = [];
    for (const c of node.children) {
      const kept = visit(c);
      if (kept) keptChildren.push(kept);
    }
    if (selfMatch || keptChildren.length > 0) {
      if (keptChildren.length > 0) expandIds.add(node.org.id);
      return { org: node.org, children: keptChildren };
    }
    return null;
  };

  const pruned: OrgNode[] = [];
  for (const root of tree) {
    const kept = visit(root);
    if (kept) pruned.push(kept);
  }
  return { tree: pruned, expandIds };
}

function countDescendants(node: OrgNode): number {
  let n = node.children.length;
  for (const c of node.children) n += countDescendants(c);
  return n;
}

export default function Organizations() {
  useDocumentTitle("Organizations");
  useSnapshot(organizationStore);
  const {
    organizationList,
    showCreateModal,
    setModalDeleteVisible,
    modalDeleteVisible,
    isDeleting,
  } = organizationStore;
  const { deleteOrg, organizationSelected, setOrganizationSelected } = useOrganization();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const service = new OrganizationService();

  const [searchTerm, setSearchTerm] = useState(searchParams.get("q") || "");
  const [expandedIds, setExpandedIds] = useState<Set<number>>(new Set());
  const [moveTarget, setMoveTarget] = useState<Organization | null>(null);
  const [openMenuId, setOpenMenuId] = useState<number | null>(null);

  useEffect(() => {
    const fetchOrganizations = async () => {
      const response = await service.getOrganizations(appStore.loggedInUser.organization.id);
      if (response) organizationStore.setOrganizations(response);
    };
    fetchOrganizations();
  }, [showCreateModal, modalDeleteVisible]);

  const fullTree = useMemo(() => buildTree(organizationList), [organizationList]);

  const { tree: visibleTree, expandIds: searchExpandIds } = useMemo(
    () => pruneTree(fullTree, searchTerm),
    [fullTree, searchTerm]
  );

  const effectiveExpanded = useMemo(() => {
    const s = new Set(expandedIds);
    searchExpandIds.forEach((id) => s.add(id));
    return s;
  }, [expandedIds, searchExpandIds]);

  const toggleExpand = (id: number) => {
    setExpandedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const expandAll = () => {
    const ids = new Set<number>();
    const walk = (nodes: OrgNode[]) => {
      for (const n of nodes) {
        if (n.children.length > 0) {
          ids.add(n.org.id);
          walk(n.children);
        }
      }
    };
    walk(fullTree);
    setExpandedIds(ids);
  };

  const collapseAll = () => setExpandedIds(new Set());

  const openCreateModal = (parentId: number | null) => {
    organizationStore.openCreateModalWithParent(parentId);
  };

  const handleMoveToParent = async (newParentId: number | null) => {
    if (!moveTarget) return;
    try {
      await service.setOrganizationParent(moveTarget.id, newParentId);
      const refreshed = await service.getOrganizations(appStore.loggedInUser.organization.id);
      if (refreshed) organizationStore.setOrganizations(refreshed);
      setMoveTarget(null);
      showAlert("Organization moved", "success", 2500);
    } catch (e: any) {
      showAlert(e?.message ?? "Failed to move organization", "error", 4000);
    }
  };

  const totalOrgs = organizationList.length;
  const topLevelCount = fullTree.length;
  const subOrgCount = totalOrgs - topLevelCount;

  return (
    <div className="px-4 py-3">
      <Message marginTop="16px" />
      <CreateOrganization />

      <div className="d-flex justify-content-between align-items-center mb-4">
        <div>
          <h3 className="mb-1 fw-bold">Organizations</h3>
          <p className="text-muted mb-0">
            Manage your organizations and sub-org hierarchies
          </p>
        </div>
        <Button
          variant="primary"
          onClick={() => openCreateModal(null)}
          className="d-flex align-items-center gap-2"
        >
          <i className="bi bi-plus-circle"></i>
          New Organization
        </Button>
      </div>

      <Card className="border-0 shadow-sm">
        <Card.Body className="pb-0">
          <div className="d-flex align-items-center gap-3 mb-3">
            <InputGroup className="flex-grow-1 mb-0">
              <InputGroup.Text className="bg-white">
                <i className="bi bi-search text-muted"></i>
              </InputGroup.Text>
              <Form.Control
                placeholder="Search organizations..."
                value={searchTerm}
                onChange={(e) => {
                  const value = e.target.value;
                  setSearchTerm(value);
                  if (value) {
                    setSearchParams({ q: value }, { replace: true });
                  } else {
                    setSearchParams({}, { replace: true });
                  }
                }}
                className="border-start-0"
              />
            </InputGroup>
            <div className="text-muted small text-nowrap">
              {topLevelCount} top-level · {subOrgCount} sub-orgs
            </div>
            <Button
              variant="light"
              size="sm"
              onClick={expandedIds.size > 0 ? collapseAll : expandAll}
              className="d-flex align-items-center gap-1 text-nowrap"
            >
              <i className={`bi ${expandedIds.size > 0 ? "bi-chevron-up" : "bi-chevron-down"}`} />
              {expandedIds.size > 0 ? "Collapse all" : "Expand all"}
            </Button>
          </div>

          <Table hover className="align-middle mb-0 org-tree-table">
            <thead>
              <tr>
                <th>Name</th>
                <th>Description</th>
                <th className="text-end" style={{ width: 140 }}>Sub-orgs</th>
                <th style={{ width: 60 }}></th>
              </tr>
            </thead>
            <tbody>
              {visibleTree.flatMap((node) =>
                renderRow(node, 0, {
                  expanded: effectiveExpanded,
                  toggleExpand,
                  navigate,
                  searchTerm,
                  openCreateModal,
                  setMoveTarget,
                  setOrganizationSelected,
                  setModalDeleteVisible,
                  openMenuId,
                  setOpenMenuId,
                })
              )}
            </tbody>
          </Table>
        </Card.Body>
      </Card>

      {visibleTree.length === 0 && (
        <div className="text-center py-5">
          <i className="bi bi-building fs-1 text-muted d-block mb-3"></i>
          <h5 className="text-muted">No organizations found</h5>
          <p className="text-muted">
            {searchTerm
              ? "Try adjusting your search"
              : "Create your first organization to get started"}
          </p>
        </div>
      )}

      <MoveToParentModal
        show={moveTarget !== null}
        org={moveTarget}
        allOrgs={organizationList}
        onClose={() => setMoveTarget(null)}
        onSubmit={handleMoveToParent}
      />

      <OrgDeleteModal
        show={modalDeleteVisible}
        org={modalDeleteVisible ? organizationSelected : null}
        allOrgs={organizationList}
        isLoading={isDeleting}
        onClose={() => setModalDeleteVisible(false)}
        onConfirm={async (mode) => { await deleteOrg(mode); }}
      />
    </div>
  );
}

interface RowCtx {
  expanded: Set<number>;
  toggleExpand: (id: number) => void;
  navigate: ReturnType<typeof useNavigate>;
  searchTerm: string;
  openCreateModal: (parentId: number | null) => void;
  setMoveTarget: (o: Organization) => void;
  setOrganizationSelected: (o: Organization) => void;
  setModalDeleteVisible: (v: boolean) => void;
  openMenuId: number | null;
  setOpenMenuId: (id: number | null) => void;
}

function renderRow(node: OrgNode, depth: number, ctx: RowCtx): JSX.Element[] {
  const hasChildren = node.children.length > 0;
  const isExpanded = ctx.expanded.has(node.org.id);
  const totalSubs = countDescendants(node);

  const row = (
    <tr
      key={node.org.id}
      style={{ cursor: "pointer" }}
      onClick={() => {
        ctx.navigate(`../${Paths.ORGANIZATION_DETAIL}/${node.org.id}`, {
          state: { q: ctx.searchTerm },
        });
      }}
    >
      <td style={{ paddingLeft: 16 + depth * 28 }}>
        <div className="d-flex align-items-center gap-2">
          {hasChildren ? (
            <Button
              variant="link"
              size="sm"
              className="p-0 tree-chevron"
              onClick={(e) => {
                e.stopPropagation();
                ctx.toggleExpand(node.org.id);
              }}
              aria-label={isExpanded ? "Collapse" : "Expand"}
            >
              <i className={`bi ${isExpanded ? "bi-chevron-down" : "bi-chevron-right"}`} />
            </Button>
          ) : (
            <span style={{ display: "inline-block", width: 20 }} />
          )}
          <span
            className={`fw-semibold ${depth > 0 ? "text-body" : ""}`}
            style={{ fontWeight: depth === 0 && hasChildren ? 700 : 600 }}
          >
            {node.org.name}
          </span>
          {depth > 0 && (
            <span className="text-muted small">
              <i className="bi bi-diagram-3 me-1" />
              sub-org
            </span>
          )}
        </div>
      </td>
      <td className="text-muted">{node.org.description || "—"}</td>
      <td className="text-end">
        {hasChildren ? (
          <span className="fw-semibold">{totalSubs}</span>
        ) : (
          <span className="text-muted">—</span>
        )}
      </td>
      <td className="text-end" onClick={(e) => e.stopPropagation()}>
        <Dropdown
          align="end"
          show={ctx.openMenuId === node.org.id}
          onToggle={(isOpen) => ctx.setOpenMenuId(isOpen ? node.org.id : null)}
        >
          <Dropdown.Toggle as={RowMenuToggle}>
            <i className="bi bi-three-dots-vertical" />
          </Dropdown.Toggle>
          <Dropdown.Menu>
            <Dropdown.Item onClick={() => ctx.openCreateModal(node.org.id)}>
              <i className="bi bi-plus-circle me-2" />
              Add sub-org
            </Dropdown.Item>
            <Dropdown.Item onClick={() => ctx.setMoveTarget(node.org)}>
              <i className="bi bi-arrows-move me-2" />
              Move to parent…
            </Dropdown.Item>
            <Dropdown.Divider />
            <Dropdown.Item
              onClick={() =>
                ctx.navigate(`../${Paths.ORGANIZATION_DETAIL}/${node.org.id}`, {
                  state: { q: ctx.searchTerm },
                })
              }
            >
              <i className="bi bi-pencil me-2" />
              Open
            </Dropdown.Item>
            <Dropdown.Item
              className="text-danger"
              onClick={() => {
                ctx.setOrganizationSelected(node.org);
                ctx.setModalDeleteVisible(true);
              }}
            >
              <i className="bi bi-trash me-2" />
              Delete
            </Dropdown.Item>
          </Dropdown.Menu>
        </Dropdown>
      </td>
    </tr>
  );

  const out: JSX.Element[] = [row];
  if (hasChildren && isExpanded) {
    for (const c of node.children) out.push(...renderRow(c, depth + 1, ctx));
  }
  return out;
}
